// Panel de administración de Feedback Beta. Solo accesible a usuarios listados
// en Feedback_Beta_Admins. Permite ver, filtrar, marcar estados, anotar y
// gestionar la lista de admins.

import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, TextField, Button, IconButton, Tooltip, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, MenuItem, Stack, Skeleton, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
    Snackbar, CircularProgress, Tabs, Tab, Badge,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import {
    feedbackBetaSoyAdminJWT, feedbackBetaListarJWT, feedbackBetaObtenerJWT,
    feedbackBetaActualizarJWT,
    feedbackBetaEliminarJWT, feedbackBetaScreenshotBlobUrl,
    feedbackBetaListarAdminsJWT, feedbackBetaAgregarAdminJWT, feedbackBetaQuitarAdminJWT,
    feedbackBetaBuscarUsuariosJWT,
} from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;

// Estados (etiqueta visible vs valor en DB):
//   nuevo       → "Nuevo"
//   leido       → "En análisis"   (visto, sin decidir)
//   pendiente   → "Pendiente"     (aprobado, falta hacer)
//   resuelto    → "Implementado"  (listo)
//   diferido    → "Diferido"      (más adelante)
//   descartado  → "Descartado"    (no aplica)
const ESTADOS_OPCIONES = [
    { v: 'nuevo', label: 'Nuevo' },
    { v: 'leido', label: 'En análisis' },
    { v: 'pendiente', label: 'Pendiente' },
    { v: 'resuelto', label: 'Implementado' },
    { v: 'diferido', label: 'Diferido' },
    { v: 'descartado', label: 'Descartado' },
];
const ESTADO_LABEL = Object.fromEntries(ESTADOS_OPCIONES.map(e => [e.v, e.label]));
const ESTADO_COLOR = {
    nuevo: 'error', leido: 'primary', pendiente: 'warning',
    resuelto: 'success', diferido: 'default', descartado: 'default',
};

// Tabs: agrupan estados para que el admin trabaje en lotes naturales.
const TABS = [
    { key: 'por_revisar', label: 'Por revisar', estados: 'nuevo,leido' },
    { key: 'pendientes',  label: 'Pendientes',  estados: 'pendiente' },
    { key: 'implementados', label: 'Implementados', estados: 'resuelto' },
    { key: 'otros',       label: 'Otros',       estados: 'diferido,descartado' },
    { key: 'todos',       label: 'Todos',       estados: 'todos' },
];

const CATEGORIAS = [
    { v: 'todas', label: 'Todas las categorías' },
    { v: 'bug', label: 'Algo falla', icon: <BugReportIcon fontSize="small" sx={{ color: '#c62828' }} /> },
    { v: 'mejora', label: 'Sugerencia', icon: <LightbulbIcon fontSize="small" sx={{ color: '#f57c00' }} /> },
    { v: 'otro', label: 'Otro', icon: <ChatIcon fontSize="small" sx={{ color: '#1769aa' }} /> },
];
const catIcon = (c) => CATEGORIAS.find(x => x.v === c)?.icon || null;

const fmtFecha = (s) => s ? new Date(s).toLocaleString('es-AR') : '';

export default function FeedbackBetaPanel() {
    const [esAdmin, setEsAdmin] = useState(null);  // null = checking
    // Tab activa = por_revisar por default (lo que requiere atención).
    const [tabKey, setTabKey] = useState('por_revisar');
    const [filtros, setFiltros] = useState({ categoria: 'todas', usuario: '' });
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(30);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [detalleId, setDetalleId] = useState(null);
    const [adminsOpen, setAdminsOpen] = useState(false);
    const [snack, setSnack] = useState('');
    // Conteos por tab (se cargan en paralelo a la lista actual).
    const [conteos, setConteos] = useState({});

    useEffect(() => {
        feedbackBetaSoyAdminJWT()
            .then(r => setEsAdmin(r?.ok && r.esAdmin === true))
            .catch(() => setEsAdmin(false));
    }, []);

    const tabActual = TABS.find(t => t.key === tabKey) || TABS[0];

    const cargar = async () => {
        setLoading(true);
        try {
            const r = await feedbackBetaListarJWT({
                ...filtros,
                estado: tabActual.estados,  // string con uno o varios estados separados por coma
                page: page + 1,
                pageSize,
            });
            if (r?.ok) { setItems(r.items || []); setTotal(r.total || 0); }
        } finally { setLoading(false); }
    };

    // Conteos por tab — pedimos el total de cada grupo para mostrar en el badge.
    // Una sola pasada con pageSize=1 alcanza (sólo necesitamos el total).
    const cargarConteos = async () => {
        const out = {};
        await Promise.all(TABS.map(async (t) => {
            try {
                const r = await feedbackBetaListarJWT({
                    estado: t.estados, categoria: filtros.categoria, usuario: filtros.usuario,
                    page: 1, pageSize: 1,
                });
                if (r?.ok) out[t.key] = r.total || 0;
            } catch { /* silent */ }
        }));
        setConteos(out);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (esAdmin === true) cargar(); }, [filtros, tabKey, page, pageSize, esAdmin]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (esAdmin === true) cargarConteos(); }, [filtros, esAdmin, items]);

    if (esAdmin === null) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
    }
    if (esAdmin === false) {
        return <Box sx={{ p: 3 }}><Alert severity="warning">No tenés acceso al panel de feedback beta.</Alert></Box>;
    }

    const eliminar = async (id) => {
        if (!window.confirm('¿Eliminar este reporte? La acción no se puede deshacer.')) return;
        const r = await feedbackBetaEliminarJWT(id);
        if (r?.ok) { setSnack('Reporte eliminado'); cargar(); }
        else setSnack(r?.mensaje || 'Error al eliminar');
    };

    return (
        <Box sx={{ p: 2.5, bgcolor: '#f6f8fc', minHeight: '100vh' }}>
            <Paper sx={{ p: 2.5, borderRadius: `${BR}px`, mb: 2,
                background: `linear-gradient(135deg, ${PRIMARY} 0%, #2196f3 100%)`, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Feedback Beta</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Reportes recibidos durante el período de pruebas
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Gestionar admins">
                        <IconButton onClick={() => setAdminsOpen(true)}
                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                            <GroupIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Refrescar">
                        <IconButton onClick={cargar}
                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Paper>

            <Paper sx={{ borderRadius: `${BR}px`, mb: 2, overflow: 'hidden' }}>
                {/* Tabs por estado: agrupan los 6 estados en 5 vistas naturales. */}
                <Tabs
                    value={tabKey}
                    onChange={(_, v) => { setTabKey(v); setPage(0); }}
                    variant="scrollable" scrollButtons="auto"
                    sx={{ borderBottom: '1px solid #e0e0e0', px: 1, minHeight: 42 }}
                >
                    {TABS.map(t => {
                        const n = conteos[t.key];
                        return (
                            <Tab key={t.key} value={t.key}
                                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 42, py: 0.5 }}
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <span>{t.label}</span>
                                        {n != null && (
                                            <Badge badgeContent={n} color={t.key === 'por_revisar' ? 'error' : 'default'}
                                                showZero={t.key === 'todos'}
                                                sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none', fontSize: 10, height: 18, minWidth: 18 } }} />
                                        )}
                                    </Stack>
                                } />
                        );
                    })}
                </Tabs>
                <Box sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                        <TextField select size="small" label="Categoría" value={filtros.categoria} sx={{ width: 200 }}
                            onChange={e => { setFiltros(f => ({ ...f, categoria: e.target.value })); setPage(0); }}>
                            {CATEGORIAS.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
                        </TextField>
                        <TextField size="small" label="Usuario" value={filtros.usuario} sx={{ width: 180 }}
                            onChange={e => setFiltros(f => ({ ...f, usuario: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') { setPage(0); cargar(); } }} />
                        <Typography variant="body2" sx={{ color: '#666', ml: 'auto' }}>
                            {total} reporte{total !== 1 ? 's' : ''} en <strong>{tabActual.label}</strong>
                        </Typography>
                    </Stack>
                </Box>
            </Paper>

            <TableContainer component={Paper} sx={{ borderRadius: `${BR}px` }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}></TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Mensaje</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6}><Skeleton variant="rectangular" height={150} /></TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: '#888' }}>Sin reportes</TableCell></TableRow>
                        ) : items.map(it => (
                            <TableRow key={it.Id} hover sx={{ cursor: 'pointer',
                                bgcolor: it.Estado === 'nuevo' ? '#fff5f5' : 'transparent' }}
                                onClick={() => setDetalleId(it.Id)}>
                                <TableCell sx={{ width: 32 }}>{catIcon(it.Categoria)}</TableCell>
                                <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{fmtFecha(it.Fecha)}</TableCell>
                                <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{it.Usuario}</TableCell>
                                <TableCell sx={{ fontSize: '0.82rem', maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {it.Mensaje}
                                    {it.Screenshot_Filename && <Chip label="📎" size="small" sx={{ ml: 1, height: 18 }} />}
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={ESTADO_LABEL[it.Estado] || it.Estado} color={ESTADO_COLOR[it.Estado] || 'default'} />
                                </TableCell>
                                <TableCell align="right" onClick={e => e.stopPropagation()}>
                                    <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => setDetalleId(it.Id)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                                    <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => eliminar(it.Id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
                    rowsPerPageOptions={[15, 30, 50, 100]} />
            </TableContainer>

            <DetalleDialog id={detalleId} onClose={() => { setDetalleId(null); cargar(); }} />
            <AdminsDialog open={adminsOpen} onClose={() => setAdminsOpen(false)} />
            <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}

// ─── Dialog de detalle ───
function DetalleDialog({ id, onClose }) {
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imgUrl, setImgUrl] = useState(null);
    const [estado, setEstado] = useState('');
    const [notas, setNotas] = useState('');
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!id) {
            setItem(null);
            // Cleanup blob URL al cerrar
            setImgUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
            return;
        }
        let cancel = false;
        setLoading(true);
        feedbackBetaObtenerJWT(id)
            .then(r => {
                if (cancel) return;
                if (r?.ok) {
                    setItem(r.item);
                    setEstado(r.item.Estado);
                    setNotas(r.item.Notas_Admin || '');
                    if (r.item.Screenshot_Filename) {
                        feedbackBetaScreenshotBlobUrl(r.item.Id)
                            .then(url => { if (!cancel) setImgUrl(url); })
                            .catch(() => {});
                    }
                    // Auto-marcar como leído si estaba nuevo (silencioso, sin esperar)
                    if (r.item.Estado === 'nuevo') {
                        feedbackBetaActualizarJWT(r.item.Id, { estado: 'leido' }).catch(() => {});
                        setEstado('leido');
                    }
                }
            })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
        // eslint-disable-next-line
    }, [id]);

    // Cleanup blob URL al desmontar el componente
    useEffect(() => {
        return () => { if (imgUrl) URL.revokeObjectURL(imgUrl); };
    }, [imgUrl]);

    const guardar = async () => {
        setGuardando(true);
        try {
            await feedbackBetaActualizarJWT(item.Id, { estado, notasAdmin: notas });
            onClose();
        } finally { setGuardando(false); }
    };

    return (
        <Dialog open={!!id} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Detalle del reporte
                <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? <Skeleton variant="rectangular" height={300} /> : item && (
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <Chip icon={catIcon(item.Categoria)} label={item.Categoria} size="small" sx={{ textTransform: 'capitalize' }} />
                            <Chip label={`#${item.Id}`} size="small" variant="outlined" />
                            <Chip label={item.Usuario} size="small" />
                            <Chip label={fmtFecha(item.Fecha)} size="small" variant="outlined" />
                        </Stack>

                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', textTransform: 'uppercase' }}>Mensaje</Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#fafafa', whiteSpace: 'pre-wrap' }}>
                            {item.Mensaje}
                        </Paper>

                        {(item.Url || item.User_Agent) && (
                            <Box sx={{ mb: 2, fontSize: '0.78rem', color: '#666' }}>
                                {item.Url && <div><strong>URL:</strong> {item.Url}</div>}
                                {item.User_Agent && <div><strong>Navegador:</strong> {item.User_Agent}</div>}
                            </Box>
                        )}

                        {imgUrl && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                    Captura
                                </Typography>
                                <img src={imgUrl} alt="screenshot" style={{ maxWidth: '100%', borderRadius: 4, border: '1px solid #ddd', cursor: 'zoom-in' }}
                                    onClick={() => window.open(imgUrl, '_blank')} />
                            </Box>
                        )}

                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                            <TextField select size="small" label="Estado" value={estado} onChange={e => setEstado(e.target.value)} sx={{ width: 220 }}>
                                {ESTADOS_OPCIONES.map(o => (
                                    <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>
                                ))}
                            </TextField>
                        </Stack>

                        <TextField label="Notas privadas (no las ve el reportante)" multiline minRows={3} maxRows={8} fullWidth
                            value={notas} onChange={e => setNotas(e.target.value)} />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
                <Button variant="contained" onClick={guardar} disabled={guardando || !item}
                    sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0d4f86' }, textTransform: 'none' }}>
                    {guardando ? 'Guardando…' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Dialog de admins ───
function AdminsDialog({ open, onClose }) {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [opciones, setOpciones] = useState([]);
    const [inputBusqueda, setInputBusqueda] = useState('');

    const cargar = async () => {
        setLoading(true);
        try {
            const r = await feedbackBetaListarAdminsJWT();
            if (r?.ok) setAdmins(r.items || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { if (open) cargar(); }, [open]);

    useEffect(() => {
        if (!inputBusqueda || inputBusqueda.length < 2) { setOpciones([]); return; }
        const t = setTimeout(async () => {
            const r = await feedbackBetaBuscarUsuariosJWT(inputBusqueda);
            if (r?.ok) setOpciones(r.items || []);
        }, 300);
        return () => clearTimeout(t);
    }, [inputBusqueda]);

    const agregar = async (usuario) => {
        if (!usuario) return;
        const r = await feedbackBetaAgregarAdminJWT(usuario);
        if (r?.ok) { cargar(); setInputBusqueda(''); }
        else alert(r?.mensaje || 'Error');
    };

    const quitar = async (usuario) => {
        if (!window.confirm(`¿Quitar a ${usuario} del panel de admins?`)) return;
        const r = await feedbackBetaQuitarAdminJWT(usuario);
        if (r?.ok) cargar();
        else alert(r?.mensaje || 'Error');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ color: PRIMARY, fontWeight: 700 }}>Admins del panel de Feedback</DialogTitle>
            <DialogContent dividers>
                <Typography variant="caption" sx={{ display: 'block', color: '#777', mb: 2 }}>
                    Los admins pueden ver y gestionar todos los reportes recibidos. eliuzzi siempre es admin (no se puede quitar).
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Autocomplete
                        size="small"
                        sx={{ flex: 1 }}
                        options={opciones}
                        getOptionLabel={(o) => o?.usuario ? `${o.usuario} — ${o.nombre || ''}` : ''}
                        filterOptions={(x) => x}
                        value={null}
                        inputValue={inputBusqueda}
                        onInputChange={(_, v) => setInputBusqueda(v)}
                        onChange={(_, v) => v?.usuario && agregar(v.usuario)}
                        renderInput={(p) => <TextField {...p} label="Buscar usuario para sumar" placeholder="Apellido o usuario… (mín 2 letras)" />}
                        isOptionEqualToValue={(o, v) => o.usuario === v.usuario}
                    />
                </Stack>

                {loading ? <Skeleton height={150} /> : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Desde</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {admins.map(a => (
                                    <TableRow key={a.Id}>
                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{a.Usuario}</TableCell>
                                        <TableCell>{a.Apellido ? `${a.Apellido}, ${a.Nombre || ''}` : '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem' }}>{fmtFecha(a.Fecha_Alta)}</TableCell>
                                        <TableCell align="right">
                                            {a.Usuario !== 'eliuzzi' && (
                                                <Tooltip title="Quitar">
                                                    <IconButton size="small" color="error" onClick={() => quitar(a.Usuario)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}
